import React, { useState, useEffect, Fragment, useMemo } from 'react';
import {
  Tabs, Input, Tooltip, Popconfirm, Badge, Alert, Popover, Button,
} from 'antd';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import './style.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen, faDownload, faFile, faFilter, faIndustry, faLayerGroup, faInfoCircle,
  faMoneyBill,
  faMoneyCheck,
} from '@fortawesome/free-solid-svg-icons';

import {
  withRouter, useLocation, useParams,
} from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import H3Text from '@Uilib/h3Text';
import Helpers from '@Apis/helpers';
import Constants, { toISTDate } from '@Apis/constants';
import AnalyticsActions from '@Actions/application/analyticsActions';
import SelectDepartment from '@Components/Common/SelectDepartment';
import { v4 as uuidv4 } from 'uuid';
import SalesOrder from './SalesOrder';
import MaterialsRequirement from './MaterialsRequirement';
import ProductionOrders from './ProductionOrders';
import mrpV2Actions from '../../../../actions/production/mrpV2Actions';
import MaterialShortage from './MaterialShortage';
import MaterialRequests from './MaterialRequests';
import UpdateMrpDepartment from './UpdateMrpDepartment';
import LinkedPurchaseRequests from './LinkedPurchaseRequests';
import documentUINamesConstant from '../../../../apis/documentUINamesConstant';
import PurchaseIndents from './PurchaseIndents';

const { TabPane } = Tabs;

const ViewMrpV2 = ({
  getMrpV2, mrpV2, getMrpV2Loading, user, history, updateMrpV2, closeMrpPlan,
  deleteMrpV2Loading, deleteMrpV2, closeMrpPlanLoading, getMrpSummaryLoading, getMrpSummary, mrpSummary,
  downloadDocument,
}) => {
  const [editName, setEditName] = useState(false);
  const [name, setName] = useState('Untitled');
  const location = useLocation();
  const { id } = location.state || {};
  const { mrpId } = useParams();
  const [call, setCall] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [callForMO, setCallforMO] = useState(false);
  const [openPopover, setOpenPopover] = useState(false);

  useEffect(() => {
    if (id || mrpId) {
      getMrpV2({
        tenantId: user?.tenant_info?.tenant_id,
        listScreen: false,
        mrpId: Number(mrpId || id),
      });
      getMrpSummary({ mrpId: Number(mrpId || id) }, () => { });
    }
  }, [id, mrpId, call, callForMO]);

  useEffect(() => {
    if (mrpV2) {
      setName(mrpV2?.mrp_name);
    }
  }, [mrpV2]);

  // Function to handle name change
  const handleNameChange = (e) => {
    setName(e.target.value);
    setEditName(false);
    updateMrpV2({
      mrp_id: mrpV2?.mrp_id,
      mrp_name: e.target.value,
    }, () => {
      setCall(!call);
    });
  };

  useEffect(() => {
    const tabQuery = new URLSearchParams(location.search).get('tab');
    setActiveTab(tabQuery);
  }, [location.search]);

  const documentUINames = useMemo(() => documentUINamesConstant(user), [user]);

  const tabsData = [
    {
      key: 'sales_order',
      tabLabel: (
        <span>
          <FontAwesomeIcon icon={faFilter} />
          &nbsp;
          {`${documentUINames.salesOrderUIName}s`}
          &nbsp;
          <Badge count={mrpSummary?.count_of_sales_order} />
        </span>
      ),
      content: <SalesOrder
        mrpData={mrpV2}
        callback={() => setCall(!call)}
        call={call}
      />,
    },
    {
      key: 'materials_requirement',
      tabLabel: (
        <span>
          <FontAwesomeIcon icon={faLayerGroup} />
          &nbsp;
          {`${documentUINames.salesOrderUIName} Items`}
        </span>
      ),
      content: <MaterialsRequirement
        moCreateCallback={() => setCallforMO(!callForMO)}
        mrpData={mrpV2}
        call={call}
      />,
      disabled: Number(mrpSummary?.count_of_sales_order) < 1,
    },
    {
      key: 'production_orders',
      tabLabel: (
        <span>
          <FontAwesomeIcon icon={faIndustry} />
          &nbsp;
          Production Orders
          &nbsp;
          <Badge count={mrpSummary?.count_of_mo} />
        </span>
      ),
      content: <ProductionOrders
        mrpData={mrpV2}
        callForMO
        setCallForMO={() => setCallforMO(!callForMO)}
        call={call}
      />,
    },
    {
      key: 'materials_shortage',
      tabLabel: (
        <span>
          <FontAwesomeIcon icon={faBoxOpen} />
          &nbsp;
          Materials Requirement
          &nbsp;
          <Badge count={Number(mrpSummary?.raw_material_count || 0) + Number(mrpSummary?.stock_items_count || 0)} />
        </span>
      ),
      content: <MaterialShortage
        mrpData={mrpV2}
        poCreateCallback={() => setCall(!call)}
        call={call}
      />,
      disabled: (Number(mrpSummary?.raw_material_count || 0) + Number(mrpSummary?.stock_items_count || 0)) < 1,
    },
    {
      key: 'materials_requests',
      tabLabel: (
        <span>
          <FontAwesomeIcon icon={faFile} />
          &nbsp;
          Purchase Orders
          &nbsp;
          <Badge count={mrpSummary?.count_of_purchase_order} />
        </span>
      ),
      content: <MaterialRequests
        mrpData={mrpV2}
        call={call}
      />,
      disabled: false,
    },
    {
      key: 'purchase_requests',
      tabLabel: (
        <span>
          <FontAwesomeIcon icon={faMoneyCheck} />
          &nbsp;
          Material Requests
          &nbsp;
          <Badge count={mrpSummary?.count_of_purchase_request} />
        </span>
      ),
      content: <LinkedPurchaseRequests
        mrpData={mrpV2}
        call={call}
      />,
      disabled: false,
    },{
      key: 'purchase_indents',
      tabLabel: (
        <span>
          <FontAwesomeIcon icon={faMoneyCheck} />
          &nbsp;
          Purchase Indents
          &nbsp;
          <Badge count={mrpSummary?.count_of_purchase_indent} />
        </span>
      ),
      content: <PurchaseIndents
        mrpData={mrpV2}
        call={call}
      />,
      disabled: false,
    },
  ];

  const handleTabChange = (key) => {
    const newPath = `/production/planning/v2/view/${id || mrpId}?tab=${key}`;
    history.push(newPath);
  };

  return (
    <div className="view-mrp__wrapper">
      <div className="view-mrp__header-wrapper">
        <div className="view-mrp__header-name">
          <div className="mrp-view__header">
            <Fragment>
              {
                (getMrpV2Loading || getMrpSummaryLoading) && (!mrpV2) ? (
                  <div className="mrp-view__header-loader">
                    <div className="mrp-view__header-loader-1 loadingBlock" />
                    <div className="mrp-view__header-loader-2 loadingBlock" />
                    <div className="mrp-view__header-loader-2 loadingBlock" />
                  </div>
                ) : (
                  <Fragment>
                    <div>
                      <div className="flex-display">
                        <H3Text text={`#${mrpV2?.mrp_number || ''}`} className="mrp-view__header-id" />
                        &nbsp;
                        <H3Text
                          text={(
                            <span>
                              {editName ? (
                                <Input
                                  autoFocus
                                  defaultValue={name || ''}
                                  onPressEnter={handleNameChange}
                                  onBlur={handleNameChange}
                                  style={{
                                    width: '100%',
                                    height: '30px',
                                    marginTop: '-5px',
                                    borderRadius: '4px',
                                  }}
                                  maxLength={50}
                                />
                              ) : (Helpers.getPermission(Helpers.permissionEntities.MRP,
                                Helpers.permissionTypes.UPDATE, user)
                                && (
                                  <Tooltip title="Click to edit name">
                                    {name ? (
                                      <div
                                        className="view-mrp-editable-name"
                                        onClick={() => setEditName(true)}
                                      >
                                        {name || ''}
                                        {' '}
                                        <EditOutlined />
                                      </div>
                                    ) : ''}
                                  </Tooltip>
                                )
                              )}
                            </span>
                          )}
                          className="document-header__mrp-name"
                        />
                        <span className="status-tag" style={{ backgroundColor: Helpers.getStatusColor(mrpV2?.status).color, margin: '0px 20px' }}>
                          {mrpV2?.status?.replaceAll('_', ' ')?.toProperCase()}
                        </span>
                      </div>
                      <H3Text text={`${mrpV2?.tenant_info?.tenant_name} | Created by ${mrpV2?.created_by_info?.first_name} at ${toISTDate(mrpV2?.created_at).format('DD MMM, YYYY hh:mm A')}`} className="mrp-view__header-date" />
                    </div>
                    <div className="filters__buttons">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="filter__button">
                        <UpdateMrpDepartment
                          mrp={mrpV2}
                          user={user}
                          openPopover={openPopover}
                          openPopoverChange={() => setOpenPopover(!openPopover)}
                          call={call}
                          setCall={setCall}
                        />
                      </div>
                      &nbsp;
                      <H3Text
                        text={(
                          <FontAwesomeIcon
                            icon={faDownload}
                            color="rgb(0,143,251)"
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                        onClick={() => downloadDocument({
                          url: `${Constants.MRP_V2}/download?mrp_id=${mrpV2?.mrp_id}`,
                          document_type: 'MATERIALS_PLAN',
                          document_number: mrpV2?.mrp_number,
                          key: uuidv4(),
                        })}
                        className="filter__button"
                      />
                      &nbsp;
                      {Helpers.getPermission(Helpers.permissionEntities.MRP,
                        Helpers.permissionTypes.UPDATE, user) && (
                        <Popconfirm
                          placement="bottomRight"
                          title={(
                            <div style={{ width: '300px' }}>
                              Are you sure you want to
                              {mrpV2?.status === 'OPEN' ? ' close' : ' open'}
                              {' '}
                              this materials plan?
                            </div>
                          )}
                          className=""
                          onConfirm={() => {
                            closeMrpPlan({
                              mrp_id: mrpV2?.mrp_id,
                              status: mrpV2?.status === 'OPEN' ? 'CLOSED' : 'OPEN',
                            }, () => {
                              setCall(!call);
                            });
                          }}
                          trigger="click"
                          getPopupContainer={(triggerNode) => triggerNode.parentNode}
                        >
                          <H3Text
                            text={(
                              <span>
                                {mrpV2?.status === 'OPEN' ? 'Mark as Closed' : 'Mark as Open'}
                                {closeMrpPlanLoading ? <LoadingOutlined /> : ''}
                              </span>
                            )}
                            className="filter__button"
                          />
                        </Popconfirm>
                      )}
                      &nbsp;
                      {Helpers.getPermission(Helpers.permissionEntities.MRP,
                        Helpers.permissionTypes.DELETE, user) && (
                        <Popconfirm
                          placement="bottomRight"
                          title={(
                            <div style={{ width: '300px' }}>
                              Are you sure you want to delete this materials plan?
                            </div>
                          )}
                          className=""
                          onConfirm={() => {
                            if (!deleteMrpV2Loading) {
                              deleteMrpV2({
                                mrpId: mrpV2?.mrp_id,
                              }, () => {
                                history.push('/production/planning/v2');
                              });
                            }
                          }}
                          trigger="click"
                          getPopupContainer={(triggerNode) => triggerNode.parentNode}
                        >
                          <H3Text
                            text={(
                              <span>
                                Delete
                                {deleteMrpV2Loading ? <LoadingOutlined /> : ''}
                              </span>
                            )}
                            className="filter__button"
                          />
                        </Popconfirm>
                      )}
                    </div>
                  </Fragment>
                )
              }
            </Fragment>
          </div>
          <div className="ant-col-md-18" />
        </div>
      </div>
      <div className="view-mrp__tabs-wrapper">
        <Tabs
          defaultActiveKey="1"
          type="card"
          className="view-mrp-tabs"
          style={{
            marginTop: '10px',
          }}
          onChange={handleTabChange}
          activeKey={activeTab || 'sales_order'}
        >
          {tabsData.map((tabs) => (
            <TabPane tab={tabs.tabLabel} key={tabs.key} disabled={tabs.disabled}>
              {tabs.content}
            </TabPane>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

const mapStateToProps = ({
  UserReducers, MrpV2Reducers,
}) => ({
  user: UserReducers.user,
  getMrpV2Loading: MrpV2Reducers.getMrpV2Loading,
  mrpV2: MrpV2Reducers.mrpV2,
  closeMrpPlanLoading: MrpV2Reducers.closeMrpPlanLoading,
  getMrpSummaryLoading: MrpV2Reducers.getMrpSummaryLoading,
  deleteMrpV2Loading: MrpV2Reducers.deleteMrpV2Loading,
  mrpSummary: MrpV2Reducers.mrpSummary,
});

const mapDispatchToProps = (dispatch) => ({
  getMrpV2: (payload, callback) => dispatch(mrpV2Actions.getMrpV2(payload, callback)),
  updateMrpV2: (payload, callback) => dispatch(mrpV2Actions.updateMrpV2(payload, callback)),
  closeMrpPlan: (payload, callback) => dispatch(mrpV2Actions.closeMrpPlan(payload, callback)),
  deleteMrpV2: (payload, callback) => dispatch(mrpV2Actions.deleteMrpV2(payload, callback)),
  getMrpSummary: (payload, callback) => dispatch(mrpV2Actions.getMrpSummary(payload, callback)),
  downloadDocument: (payload, document) => dispatch(AnalyticsActions.downloadDocument(payload, document)),
});

ViewMrpV2.propTypes = {
  user: PropTypes.any,
  getMrpV2: PropTypes.func,
  getMrpV2Loading: PropTypes.bool,
  mrpV2: PropTypes.any,
  updateMrpV2: PropTypes.func,
  history: PropTypes.any,
  closeMrpPlan: PropTypes.func,
  closeMrpPlanLoading: PropTypes.bool,
  getMrpSummaryLoading: PropTypes.bool,
  getMrpSummary: PropTypes.func,
  mrpSummary: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ViewMrpV2));
